import { isValidElement, type ReactElement, type ReactNode } from 'react';

/**
 * Text aus einem beliebigen ReactNode ziehen, damit Spalten ohne eigenes
 * `filterValue` trotzdem filterbar sind.
 */
export function nodeToText(node: ReactNode): string {
  try {
    return extractText(node).replace(/\s+/g, ' ').trim();
  } catch {
    // Spec: darf nie werfen, egal was hereinkommt.
    return '';
  }
}

function extractText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return '';
  }
  if (typeof node === 'string') {
    return node;
  }
  if (typeof node === 'number' || typeof node === 'bigint') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map((part: ReactNode) => extractText(part)).join(' ');
  }
  if (isValidElement(node)) {
    return extractText(elementChildren(node));
  }
  if (typeof node === 'object' && Symbol.iterator in node) {
    const parts: string[] = [];
    for (const part of node) {
      parts.push(extractText(part));
    }
    return parts.join(' ');
  }
  return '';
}

function elementChildren(node: ReactElement): ReactNode {
  const props: unknown = node.props;
  if (typeof props === 'object' && props !== null && 'children' in props) {
    return (props as { children?: ReactNode }).children;
  }
  return undefined;
}

/** Diakritika entfernen: "Müller" -> "muller", "Café" -> "cafe". */
export function stripDiacritics(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .toLowerCase();
}

/** Umlaute ausschreiben: "Müller" -> "mueller", "Straße" -> "strasse". */
export function expandUmlauts(value: string): string {
  const expanded = value
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');
  // Restliche Akzente (é, ñ, …) wie in stripDiacritics behandeln.
  return stripDiacritics(expanded);
}

/**
 * Enthält-Vergleich, unempfindlich gegen Gross/Klein und gegen die
 * Schreibweise von Umlauten (beide Richtungen: "muller" und "mueller"
 * finden "Müller").
 */
export function textMatches(haystack: string, needle: string): boolean {
  if (needle.trim() === '') {
    return true;
  }
  return (
    stripDiacritics(haystack).includes(stripDiacritics(needle)) ||
    expandUmlauts(haystack).includes(expandUmlauts(needle))
  );
}

/** Distinkte, sortierte Filterwerte einer Spalte (leere Strings entfallen). */
export function distinctValues(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (trimmed === '' || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    result.push(trimmed);
  }
  result.sort((a, b) => a.localeCompare(b, 'de', { numeric: true, sensitivity: 'base' }));
  return result;
}
